import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Pencil, Trash2, ToggleLeft, ToggleRight } from 'lucide-react';
import { Button } from '@vnetwork/ui/primitives';
import { ConfirmDialog } from '@vnetwork/ui/feedback';
import { useSupplier, useSupplierMutations, useToggleActive } from '@/api/capacities';
import type { InfoItem } from '@/pages/account/components/InfoGrid';
import { MasterDetailScaffold } from '../shared/master-detail-scaffold';
import { formatWhDateTime } from '../shared/wh-format';
import { activeStatusCell } from '../shared/masters-status';

export interface CapacitySupplierDetailPageProps {
  /** Supplier id. Falls back to the `:id` route param when omitted. */
  id?: string;
}

/**
 * Supplier detail — identity header (`#code` + name + active pill) over an info
 * card (code · name · address · created/updated). Actions: Edit, Toggle Active,
 * Delete (with confirmation). Contact fields (contact_person/email/phone/tax_code/
 * website/notes) are shown when present in the API response.
 */
export default function CapacitySupplierDetailPage({ id: idProp }: CapacitySupplierDetailPageProps) {
  const params = useParams();
  const navigate = useNavigate();
  const id = idProp ?? params.id ?? '';
  const [deleteOpen, setDeleteOpen] = useState(false);

  const { data: supplier, isLoading, isError, refetch } = useSupplier(id);
  const { remove } = useSupplierMutations();
  const toggleActive = useToggleActive('suppliers');

  const s = supplier as typeof supplier & {
    contact_person?: string;
    email?: string;
    phone?: string;
    tax_code?: string;
    website?: string;
    notes?: string;
  };

  // Legacy always shows Code/Name/Contact/Email/Phone/Status/dates; tax code,
  // website, address and notes only when present.
  const items: InfoItem[] = [
    { label: 'Supplier Code', value: supplier?.code || '—' },
    { label: 'Supplier Name', value: supplier?.name || '—' },
    { label: 'Contact Person', value: s?.contact_person || '—' },
    { label: 'Email', value: s?.email || '—' },
    { label: 'Phone', value: s?.phone || '—' },
    ...(s?.tax_code ? [{ label: 'Tax Code', value: s.tax_code }] : []),
    ...(s?.website ? [{ label: 'Website', value: s.website }] : []),
    { label: 'Status', value: activeStatusCell(supplier?.is_active) },
    ...(supplier?.address ? [{ label: 'Address', value: supplier.address, full: true }] : []),
    ...(s?.notes ? [{ label: 'Notes', value: s.notes, full: true }] : []),
    { label: 'Created At', value: formatWhDateTime(supplier?.created_at) },
    { label: 'Updated At', value: formatWhDateTime(supplier?.updated_at) },
  ];

  const handleDelete = () => {
    remove.mutate(id, {
      onSuccess: () => navigate('/capacities/suppliers'),
    });
  };

  const isActive = supplier?.is_active ?? false;

  return (
    <>
      <MasterDetailScaffold
        isLoading={isLoading}
        isError={isError}
        onRetry={() => refetch()}
        loaded={!!supplier}
        backTo="/capacities/suppliers"
        backLabel="Suppliers"
        codePrefix={supplier?.code ? `#${supplier.code}` : undefined}
        title={supplier?.name ?? ''}
        status={activeStatusCell(supplier?.is_active)}
        actions={
          <>
            <Button
              variant="secondary"
              size="sm"
              startIcon={isActive ? <ToggleRight size={15} /> : <ToggleLeft size={15} />}
              loading={toggleActive.isPending}
              disabled={toggleActive.isPending}
              onClick={() => toggleActive.mutate(id)}
            >
              {isActive ? 'Deactivate' : 'Activate'}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              startIcon={<Pencil size={15} />}
              onClick={() => navigate(`/capacities/suppliers/${id}/edit`)}
            >
              Edit
            </Button>
            <Button
              variant="danger"
              size="sm"
              startIcon={<Trash2 size={15} />}
              onClick={() => setDeleteOpen(true)}
            >
              Delete
            </Button>
          </>
        }
        cardTitle="Supplier Information"
        items={items}
      />
      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Delete this supplier?"
        description={`"${supplier?.name}" will be permanently removed. This cannot be undone.`}
        destructive
        confirmLabel="Delete Supplier"
        loading={remove.isPending}
        onConfirm={handleDelete}
      />
    </>
  );
}
